import React, { useRef, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  InputAdornment,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import { useSnackbar } from 'notistack';
import { saveAs } from 'file-saver';
import axiosInstance, { jsonHeaders } from '@/api/axiosInstance';
import { environment } from '@/config/environment';
import { logger } from '@/services/logging';
import { LoadingButton } from '@/components/shared/LoadingButton';
import { COMPACT_DIALOG_PAPER_SX } from '@/components/shared/dialogStyles';

const BULK_ORDER_TEMPLATE_FORMAT = 'BULK_ORDER';
const BULK_ORDER_UPLOAD_ENDPOINT = '/bulkorders/upload';
const BULK_ORDER_TEMPLATE_DOWNLOAD_ENDPOINT = '/bulkordertemplate/get';

export interface UploadDialogResult {
  status?: string;
  rowCount?: number;
  errors?: Array<Record<string, unknown> | string>;
  errorMessage?: string;
  fileName: string;
  [key: string]: unknown;
}

interface Props {
  open: boolean;
  fileNamePrefix?: string;
  source: string;
  userId: string;
  onClose: () => void;
  onComplete: (result: UploadDialogResult) => void;
}

interface UploadFeedback {
  severity: 'success' | 'error';
  title: string;
  message: string;
}

export const UploadDialog: React.FC<Props> = ({
  open,
  fileNamePrefix = 'order-entry-template',
  source,
  userId,
  onClose,
  onComplete,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [uploadFile, setUploadFile] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [feedback, setFeedback] = useState<UploadFeedback | null>(null);

  const toBase64 = (bytes: Uint8Array) => {
    let binary = '';

    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });

    return window.btoa(binary);
  };

  const reset = () => {
    setFile(null);
    setUploadFile(null);
    setIsUploading(false);
    setFeedback(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => { onClose(); };

  const handleDialogClose = (
    _event: {},
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (isUploading && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }

    handleClose();
  };

  const uploadEnabled = !!file && !!uploadFile && !isUploading;

  const buildTemplateFilename = () => `${fileNamePrefix}_${new Date().toISOString().slice(0, 10)}.csv`;

  const hasUploadErrors = (result: UploadDialogResult) => (
    !!result.errorMessage || (Array.isArray(result.errors) && result.errors.length > 0)
  );

  const buildUploadErrorMessage = (result: UploadDialogResult) => {
    if (result.errorMessage) {
      return result.errorMessage;
    }

    const firstError = Array.isArray(result.errors) ? result.errors[0] : undefined;

    if (typeof firstError === 'string' && firstError.trim()) {
      return firstError;
    }

    if (firstError && typeof firstError === 'object') {
      const message = (firstError as { message?: string; errorMessage?: string; error?: string }).message
        ?? (firstError as { message?: string; errorMessage?: string; error?: string }).errorMessage
        ?? (firstError as { message?: string; errorMessage?: string; error?: string }).error;

      if (message?.trim()) {
        return message;
      }
    }

    return 'Upload failed';
  };

  const buildUploadSuccessMessage = (result: UploadDialogResult) => {
    if (typeof result.rowCount === 'number') {
      return `Upload completed successfully. ${result.rowCount} Order${result.rowCount === 1 ? '' : 's'} created.`;
    }

    if (typeof result.status === 'string' && result.status.trim()) {
      return result.status;
    }

    return 'Upload completed successfully.';
  };

  const showConfirmation = (nextFeedback: UploadFeedback, result: UploadDialogResult) => {
    setFeedback(nextFeedback);
    onComplete(result);
  };

  const handleDownload = async () => {
    try {
      const { data } = await axiosInstance.post(
        BULK_ORDER_TEMPLATE_DOWNLOAD_ENDPOINT,
        { source },
        {
          headers: jsonHeaders(),
          responseType: 'blob',
        },
      );

      const blob = data instanceof Blob ? data : new Blob([data]);
      saveAs(blob, buildTemplateFilename());
      handleClose();
    } catch (error) {
      logger.error('Bulk order template download failed', error as Error);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    setFile(selectedFile);
    setUploadFile(null);
    setFeedback(null);

    if (!selectedFile) {
      return;
    }

    try {
      const arrayBuffer = await selectedFile.arrayBuffer();
      setUploadFile(toBase64(new Uint8Array(arrayBuffer)));
    } catch (error) {
      logger.error('Failed to read selected upload file', error as Error);
    }
  };

  const normalizeUploadError = (error: unknown, fileName: string): UploadDialogResult => {
    const httpError = error as {
      response?: {
        data?: {
          errors?: Array<Record<string, unknown> | string>;
          message?: string;
          errorMessage?: string;
          [key: string]: unknown;
        } | string;
        statusText?: string;
      };
      message?: string;
    };
    const responseData = httpError.response?.data;

    if (typeof responseData === 'string' && responseData.trim()) {
      return {
        errorMessage: responseData,
        fileName,
      };
    }

    if (responseData && typeof responseData === 'object') {
      const responsePayload = responseData as {
        errors?: Array<Record<string, unknown> | string>;
        message?: string;
        errorMessage?: string;
        [key: string]: unknown;
      };

      if (responsePayload.errors) {
        return {
          ...responsePayload,
          fileName,
        };
      }

      if (responsePayload.errorMessage || responsePayload.message) {
        return {
          ...responsePayload,
          errorMessage: responsePayload.errorMessage || responsePayload.message,
          fileName,
        };
      }
    }

    return {
      errorMessage: httpError.response?.statusText || httpError.message || 'Upload failed',
      fileName,
    };
  };

  const handleUpload = async () => {
    if (!file || !uploadFile) {
      return;
    }

    if (!/\.csv$/i.test(file.name)) {
      const result = {
        errorMessage: 'The file is not in CSV format',
        fileName: file.name,
      };
      showConfirmation({
        severity: 'error',
        title: 'Mass Orders Upload Failed',
        message: result.errorMessage,
      }, result);
      return;
    }

    setIsUploading(true);

    try {
      const { data } = await axiosInstance.post(
        BULK_ORDER_UPLOAD_ENDPOINT,
        {
          data: uploadFile,
          orderCreatorName: userId,
        },
        {
          headers: jsonHeaders(),
          timeout: environment.uploadTimeout,
        },
      );

      const result = {
        ...(data as Record<string, unknown>),
        fileName: file.name,
      } as UploadDialogResult;

      if (hasUploadErrors(result)) {
        showConfirmation({
          severity: 'error',
          title: 'Mass Orders Upload Failed',
          message: buildUploadErrorMessage(result),
        }, result);
        return;
      }

      const successMessage = buildUploadSuccessMessage(result);
      showConfirmation({
        severity: 'success',
        title: 'Mass Orders Upload Complete',
        message: successMessage,
      }, result);
      enqueueSnackbar(successMessage, { variant: 'success' });
    } catch (error) {
      logger.error('Bulk order upload failed', error as Error);
      const result = normalizeUploadError(error, file.name);
      showConfirmation({
        severity: 'error',
        title: 'Mass Orders Upload Failed',
        message: buildUploadErrorMessage(result),
      }, result);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={isUploading}
      maxWidth={false}
      slotProps={{
        transition: {
          onExited: reset,
        },
        paper: {
          sx: COMPACT_DIALOG_PAPER_SX,
        },
      }}
    >
      <Box sx={{ m: 3 }}>
        {!feedback ? (
          <>
            <DialogTitle
              id="qa-uploadDialog-title-lbl"
              sx={{
                p: 0,
                color: 'rgba(0, 0, 0, 0.87)',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '20px',
                fontWeight: 500,
                letterSpacing: '.25px',
                lineHeight: '26px',
              }}
            >
              Mass Upload Orders
            </DialogTitle>

            <DialogContent sx={{ px: 0, py: 0, mt: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Typography
                  id="qa-uploadDialog-message-lbl"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.6)',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '16px',
                    letterSpacing: '.5px',
                    lineHeight: '28px',
                  }}
                >
                  Upload must match the Order Entry Template
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                  <Button
                    id="qa-uploadDialog-downloadTemplate-btn"
                    variant="contained"
                    onClick={handleDownload}
                    disabled={isUploading}
                    sx={{
                      maxHeight: '36px',
                      color: '#0c479d !important',
                      backgroundColor: '#fff !important',
                      boxShadow: '0px 3px 1px -2px rgba(0, 0, 0, .2), 0px 2px 2px 0px rgba(0, 0, 0, .14), 0px 1px 5px 0px rgba(0, 0, 0, .12)',
                      fontWeight: 500,
                      letterSpacing: '.0892857143em',
                      textTransform: 'none',
                      transition: 'none !important',
                      animation: 'none !important',
                      '&:hover': {
                        backgroundColor: '#fff',
                        boxShadow: '0px 2px 4px -1px rgba(0, 0, 0, .2), 0px 4px 5px 0px rgba(0, 0, 0, .14), 0px 1px 10px 0px rgba(0, 0, 0, .12)',
                      },
                    }}
                  >
                    DOWNLOAD TEMPLATE
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                  <TextField
                    fullWidth
                    id="qa-uploadDialog-fileInput-ipt"
                    label="Upload File"
                    value={file?.name ?? ''}
                    onClick={() => inputRef.current?.click()}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <FolderIcon sx={{ cursor: 'pointer' }} onClick={() => inputRef.current?.click()} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".csv"
                    hidden
                    onChange={handleFileChange}
                  />
                </Box>
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                fontFamily: 'Roboto, sans-serif',
                m: 0,
                mt: 3,
                p: '0 !important',
                gap: '12px',
                display: 'flex',
                position: 'relative',
                flexShrink: 0,
                flexWrap: 'wrap',
                alignItems: 'center',
                boxSizing: 'border-box',
                minHeight: '52px',
                borderTop: '1px solid rgba(0, 0, 0, 0)',
                justifyContent: 'flex-start',
              }}
            >
              <Button
                id="qa-uploadDialog-cancel-btn"
                onClick={handleClose}
                disabled={isUploading}
                sx={{
                  maxHeight: '36px',
                  px: '12px',
                  color: '#0c479d !important',
                  backgroundColor: '#fff !important',
                  boxShadow: 'none !important',
                  border: 'none',
                  fontWeight: 500,
                  letterSpacing: '.0892857143em',
                  textTransform: 'none',
                  transition: 'none !important',
                  animation: 'none !important',
                  '&:hover': {
                    backgroundColor: '#fff',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#fff',
                    color: 'rgba(0, 0, 0, .38)',
                    boxShadow: 'none',
                    opacity: 1,
                  },
                }}
              >
                CANCEL
              </Button>
              <LoadingButton
                id="qa-uploadDialog-upload-btn"
                variant="contained"
                disableElevation
                onClick={handleUpload}
                loading={isUploading}
                loadingText="UPLOADING"
                disabled={!uploadEnabled}
                sx={{
                  maxHeight: '36px',
                  letterSpacing: '0.08em',
                  color: 'common.white',
                  backgroundColor: '#0c479d !important',
                  fontWeight: 700,
                  textTransform: 'none',
                  transition: 'none !important',
                  animation: 'none !important',
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(0, 0, 0, .12) !important',
                    color: 'rgba(0, 0, 0, .38)',
                  },
                }}
              >
                UPLOAD ORDERS
              </LoadingButton>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent sx={{ px: 0, py: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2, py: 1 }}>
                {feedback.severity === 'success' ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'success.main' }} />
                ) : (
                  <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main' }} />
                )}

                <Typography
                  id="qa-uploadConfirm-title-lbl"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.87)',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '20px',
                    fontWeight: 500,
                    letterSpacing: '.25px',
                    lineHeight: '32px',
                  }}
                >
                  {feedback.title}
                </Typography>

                <Typography
                  id="qa-uploadConfirm-message-lbl"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.54)',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    letterSpacing: '.5px',
                    lineHeight: '28px',
                  }}
                >
                  {feedback.message}
                </Typography>

                {file?.name && (
                  <Typography id="qa-uploadDialog-confirmation-file-lbl" sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '14px' }}>
                    File: {file.name}
                  </Typography>
                )}
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                fontFamily: 'Roboto, sans-serif',
                m: 0,
                mt: 3,
                p: '0 !important',
                justifyContent: 'center',
              }}
            >
              <Button
                id="qa-uploadDialog-confirmation-close-btn"
                variant="contained"
                onClick={handleClose}
                sx={{
                  minWidth: '96px',
                  color: 'common.white',
                  backgroundColor: '#0c479d !important',
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                OKAY
              </Button>
            </DialogActions>
          </>
        )}
      </Box>
    </Dialog>
  );
};
